/**
 * Hero — per-line slide-up reveal for the `hero` page-builder block.
 *
 * Each animated "line" lives inside an overflow:hidden mask and starts
 * translated 100% below its mask. GSAP slides each inner element up to y:0
 * with a short stagger so lines overlap during the reveal — each line appears
 * to emerge from behind the line below it.
 *
 * Lines come from two sources, in document order:
 *   1. Manual wrappers in the Antlers template: `.line-mask > .line-inner`
 *      (overline, statement-layout site-name flourish).
 *   2. SplitText-generated per-visual-line wrappers on `[data-hero-split]`
 *      elements (headline + subheadline) — SplitText adds overflow:hidden
 *      masks automatically via its `mask: 'lines'` option.
 *
 * Split targets are hidden via CSS (`visibility: hidden`) until SplitText has
 * wrapped their lines, preventing a flash of unsplit text while GSAP loads.
 *
 * Runs on initial page load (from app.js `loadGsap`) and on every Swup
 * `page:view` (so it re-inits after content replace). Respects
 * prefers-reduced-motion. Falls back gracefully if GSAP or SplitText are
 * unavailable.
 */

let currentTimelines = [];
let currentSplits = [];

export function initHeroReveal() {
    const sections = Array.from(document.querySelectorAll('[data-hero-reveal]'));
    if (!sections.length) return;

    cleanup();

    const gsap = window.gsap;
    const SplitText = window.SplitText;
    if (!gsap) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    sections.forEach((section) => {
        const splitTargets = Array.from(section.querySelectorAll('[data-hero-split]'));

        // Wait for fonts so SplitText measures line breaks against the real metrics.
        // document.fonts.ready resolves immediately when fonts are already loaded.
        document.fonts.ready.then(() => {
            // Section may have been swapped out mid-wait by Swup.
            if (!document.contains(section)) return;

            if (SplitText) {
                splitTargets.forEach((el) => {
                    const split = new SplitText(el, {
                        type: 'lines',
                        linesClass: 'hero-line',
                        mask: 'lines',
                    });
                    currentSplits.push(split);
                });
            }

            // Reveal split-target containers (CSS kept them hidden to avoid the
            // unsplit-text flash). Safe to unhide now even if SplitText was missing —
            // the template still renders readable text.
            gsap.set(splitTargets, { visibility: 'visible' });

            const lines = Array.from(
                section.querySelectorAll('.line-inner, .hero-line'),
            );

            if (!lines.length) return;

            if (reducedMotion) {
                gsap.set(lines, { yPercent: 0, y: 0 });
                return;
            }

            // Unify initial state across manual .line-inner (pre-translated by CSS)
            // and SplitText .hero-line (default y:0). Start 100% of own height plus
            // 0.25em to match the mask's padding-bottom bleed zone — without the
            // extra 0.25em, the bottom of each line would still be visible inside
            // the bleed area before the reveal plays.
            gsap.set(lines, { yPercent: 100, y: '0.25em' });

            const tl = gsap.timeline({ delay: 0.1 });
            tl.to(lines, {
                yPercent: 0,
                y: 0,
                duration: 0.85,
                stagger: 0.09,
                ease: 'power3.out',
            });
            currentTimelines.push(tl);
        });
    });
}

function cleanup() {
    currentTimelines.forEach((tl) => tl.kill());
    currentTimelines = [];
    currentSplits.forEach((s) => {
        try {
            s.revert();
        } catch (e) {
            // SplitText instance may be orphaned after DOM swap — ignore.
        }
    });
    currentSplits = [];
}
